import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/client';
import { resolveUrl } from 'src/queries/__generated__/resolveUrl';
import { fireGtm } from 'src/features/analytics/hooks/useGtm';
import { shallowEqual, useSelector } from 'react-redux';
import { AppendState as UserAppendState } from 'src/core/state/user';
import { AppendState } from 'src/core/state/user/edit';

import urlResolver from '../../queries/urlResolver.graphql';

type Props = {
    enabled: boolean;
    id: string | null;
};

const selector = (s: AppendState<UserAppendState>) => {
    return {
        id: s.user.data?.id,
    };
};

export const GoogleTagManager: React.FC<Props> = (props) => {
    const { id } = useSelector(selector, shallowEqual);
    const [scripts, setScripts] = useState<{ script: string; noscript: string } | null>(null);
    const { pathname } = useLocation();

    const url = pathname.includes('/checkout')
        ? pathname.includes('/checkout/success')
            ? '/checkout/success'
            : '/checkout'
        : pathname.includes('/customer')
          ? '/customer'
          : pathname.includes('/customisation/')
            ? '/customisation'
            : pathname;

    const { data } = useQuery<resolveUrl>(urlResolver, {
        ssr: false,
        variables: {
            urlKey: url,
        },
    });
    const pageType = data?.urlResolver?.type;

    /**
     * Trigger the scripts to be added once
     */

    useEffect(() => {
        const win = window as any;
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        });

        setScripts({
            script: 'https://www.googletagmanager.com/gtm.js?id=' + props.id,
            noscript: 'https://www.googletagmanager.com/ns.html?id=' + props.id,
        });
    }, [props.id]);

    useEffect(() => {
        fireGtm({ pageType: pageType, userId: String(id) || 'null' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageType]);

    useEffect(() => {
        if (!scripts) return;
        const scriptEl = document.createElement('script');
        scriptEl.src = scripts.script;
        scriptEl.async = true;
        document.head.appendChild(scriptEl);
        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<iframe src="${scripts.noscript}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);
        return () => {
            scriptEl.remove();
            noscript.remove();
        };
    }, [scripts]);

    return null;
};
